/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { remoteSessionsAttachBinding } from "../funcs/remoteSessionsAttachBinding.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { PrincipalRemoteSessionBinding } from "../models/components/principalremotesessionbinding.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  AttachBindingRequest,
  AttachBindingSecurity,
} from "../models/operations/attachbinding.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RemoteSessionsAttachBindingMutationVariables = {
  request: AttachBindingRequest;
  security?: AttachBindingSecurity | undefined;
  options?: RequestOptions;
};

export type RemoteSessionsAttachBindingMutationData =
  PrincipalRemoteSessionBinding;

export type RemoteSessionsAttachBindingMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * attachBinding remoteSessions
 *
 * @remarks
 * Manage exact remote session attachments for an agent. Requires an ordinary human session, agent authorization authority and ownership of the upstream session. Never returns credentials.
 */
export function useRemoteSessionsAttachBindingMutation(
  options?: MutationHookOptions<
    RemoteSessionsAttachBindingMutationData,
    RemoteSessionsAttachBindingMutationError,
    RemoteSessionsAttachBindingMutationVariables
  >,
): UseMutationResult<
  RemoteSessionsAttachBindingMutationData,
  RemoteSessionsAttachBindingMutationError,
  RemoteSessionsAttachBindingMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRemoteSessionsAttachBindingMutation(client, options),
    ...options,
  });
}

export function mutationKeyRemoteSessionsAttachBinding(): MutationKey {
  return ["@gram/client", "remoteSessions", "attachBinding"];
}

export function buildRemoteSessionsAttachBindingMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RemoteSessionsAttachBindingMutationVariables,
  ) => Promise<RemoteSessionsAttachBindingMutationData>;
} {
  return {
    mutationKey: mutationKeyRemoteSessionsAttachBinding(),
    mutationFn: function remoteSessionsAttachBindingMutationFn({
      request,
      security,
      options,
    }): Promise<RemoteSessionsAttachBindingMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(remoteSessionsAttachBinding(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
